import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import logoAsset from "@/assets/sa-lexpractiq-logo.png.asset.json";

const NAV = [
  { to: "/lexpractiq", label: "Home" },
  { to: "/lexpractiq/simulations", label: "Simulations" },
  { to: "/lexpractiq/how-it-works", label: "How it works" },
  { to: "/lexpractiq/for-employers", label: "Employers" },
  { to: "/lexpractiq/for-universities", label: "Universities" },
] as const;

export function LxpLayout({ children }: { children: ReactNode }) {
  return (
    <div className="lxp-scope" style={{ position: "relative", isolation: "isolate" }}>
      <div
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          zIndex: -1,
          pointerEvents: "none",
          backgroundImage: `url(${logoAsset.url})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundSize: "min(72vmin, 720px)",
          opacity: 0.06,
          filter: "blur(0.5px)",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          zIndex: -1,
          pointerEvents: "none",
          background:
            "radial-gradient(circle at 20% 15%, rgba(212,175,55,0.08), transparent 45%), radial-gradient(circle at 85% 85%, rgba(16,185,129,0.08), transparent 45%)",
        }}
      />
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 40,
          backdropFilter: "blur(10px)",
          background: "rgba(10,14,26,0.85)",
          borderBottom: "1px solid var(--lxp-border)",
        }}
      >
        <div
          className="lxp-container"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            padding: "0.9rem 1.25rem",
          }}
        >
          <Link
            to="/lexpractiq"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              textDecoration: "none",
              color: "var(--lxp-text)",
            }}
          >
            <img
              src={logoAsset.url}
              alt="SA LexPractiq"
              width={40}
              height={40}
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                objectFit: "cover",
                boxShadow: "0 2px 12px rgba(212,175,55,0.35)",
              }}
            />
            <span style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: "-0.01em" }}>
              SA <span style={{ color: "var(--lxp-accent)" }}>LexPractiq</span>
            </span>
          </Link>
          <nav
            style={{
              display: "none",
              gap: "1.5rem",
              alignItems: "center",
            }}
            className="lxp-nav-desktop"
          >
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                style={{
                  color: "var(--lxp-text-muted)",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
                activeProps={{ style: { color: "var(--lxp-accent)" } }}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
            <Link to="/lexpractiq/dashboard" className="lxp-btn-ghost">
              My workspace
            </Link>
            <Link
              to="/auth"
              search={{ portal: "lxp", redirect: "/lexpractiq/dashboard" } as any}
              className="lxp-btn"
            >
              Sign in
            </Link>
          </div>
        </div>
        <style>{`@media (min-width: 900px){.lxp-nav-desktop{display:flex !important;}}`}</style>
      </header>
      <main>{children}</main>
      <footer
        style={{
          marginTop: "5rem",
          borderTop: "1px solid var(--lxp-border)",
          padding: "3rem 0 2rem",
          background: "var(--lxp-surface)",
        }}
      >
        <div className="lxp-container" style={{ display: "grid", gap: "2rem", gridTemplateColumns: "2fr 1fr 1fr" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.75rem" }}>
              <img
                src={logoAsset.url}
                alt="SA LexPractiq"
                width={32}
                height={32}
                style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover" }}
              />
              <strong>SA LexPractiq</strong>
            </div>
            <p className="lxp-muted" style={{ fontSize: "0.85rem", maxWidth: 420 }}>
              South Africa's AI-powered Legal Experience Platform. Learn by doing, build your portfolio, launch your legal career.
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--lxp-accent)", marginBottom: "0.75rem" }}>
              Platform
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "0.4rem", fontSize: "0.85rem" }} className="lxp-muted">
              <li><Link to="/lexpractiq/simulations">Simulations</Link></li>
              <li><Link to="/lexpractiq/how-it-works">How it works</Link></li>
              <li><Link to="/lexpractiq/for-employers">For employers</Link></li>
              <li><Link to="/lexpractiq/for-universities">For universities</Link></li>
            </ul>
          </div>
          <div>
            <h4 style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--lxp-accent)", marginBottom: "0.75rem" }}>
              Legal
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "0.4rem", fontSize: "0.85rem" }} className="lxp-muted">
              <li><Link to="/disclaimer">Disclaimer</Link></li>
              <li><Link to="/privacy">Privacy</Link></li>
              <li><Link to="/popia">POPIA</Link></li>
              <li><Link to="/">Nkadimeng Danny Legal</Link></li>
            </ul>
          </div>
        </div>
        <div className="lxp-container" style={{ marginTop: "2rem", fontSize: "0.75rem", color: "var(--lxp-text-muted)" }}>
          © {new Date().getFullYear()} SA LexPractiq — a Nkadimeng Danny Legal &amp; Compliance Support initiative. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
